import { useAuth0 } from '@auth0/auth0-vue'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useTOOStore } from './too'

const DATASOURCES_TO_COPY = [
  'devices',
  'Action Intent',
  'styles',
  'fit types',
  'list bullet types',
  'tagManagerEvents',
  'verticalPadding',
  'sizes',
]

const DEFAULT_BLOCKS_TO_DELETE = ['feature', 'grid', 'page', 'teaser']

export const useStoryblokStore = defineStore('storyblok', () => {
  const { getAccessTokenSilently } = useAuth0()
  const tooStore = useTOOStore()

  const spaces = ref([])

  // Fields used to create new site in Storyblok
  const siteSlug = ref(null)
  const space = ref(null)
  const company = ref(null)
  const pages = ref(null)
  const heroBannerTemplate = ref(null)
  const testimonialsTemplate = ref(null)

  const progress = ref(0)
  const generationLogs = ref([])
  const folderCreated = ref(null)
  const pagesCreated = ref([])
  const error = ref(null)

  watch(siteSlug, (slug) => {
    if (slug) {
      fetchSpaces()
    }
  })

  async function checkIfSiteExists(spaceId, siteSlug) {
    console.log(`Checking if site "${siteSlug}" exists in space "${spaceId}"`)

    try {
      const headers = await generateHeaders()
      const params = new URLSearchParams({
        by_slugs: siteSlug,
        is_folder: true,
      })

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/storyblok/spaces/${spaceId}/stories?${params.toString()}`,
        {
          headers,
        },
      )
      const data = await response.json()
      const matches = data?.stories
      return !!matches.length
    } catch (e) {
      console.log(e)
      return true
    }
  }

  async function generateSite() {
    try {
      progress.value = 1
      generationLogs.value = []
      pagesCreated.value = []
      folderCreated.value = null
      error.value = null

      generationLogs.value.push({
        timestamp: new Date().toISOString(),
        message: `Starting generation of site "${siteSlug.value}" in space "${space.value.name}"...`,
      })

      await migrateBlocks()
      progress.value = 30

      await configureSpace()
      progress.value = 50

      await migrateDatasources()
      progress.value = 80

      await copySourceFolder()
      progress.value = 90

      await generateAccessToken()

      return true
    } catch (e) {
      console.log(e)

      generationLogs.value.push({
        timestamp: new Date().toISOString(),
        message: `ERROR: ${e.message}`,
      })

      if (folderCreated.value?.id) {
        try {
          const headers = await generateHeaders()
          const deleteResponse = await fetch(
            `${import.meta.env.VITE_API_URL}/storyblok/spaces/${space.value.id}/stories/${folderCreated.value?.id}`,
            {
              method: 'DELETE',
              headers,
            },
          )
          if (!deleteResponse.ok) {
            generationLogs.value.push({
              timestamp: new Date().toISOString(),
              message: `ERROR: Could not delete folder in target space after error.`,
            })
          }
        } catch (e) {
          console.log(`Could not delete folder in target space`)
          console.error(e)
        }
      }

      error.value = e

      return false
    } finally {
      progress.value = 100
    }
  }

  function restart() {
    progress.value = 0
    generationLogs.value = []
    pagesCreated.value = []
    folderCreated.value = null
    error.value = null
  }

  async function fetchSpaces() {
    if (spaces.value.length) return spaces.value

    const headers = await generateHeaders()
    const response = await fetch(`${import.meta.env.VITE_API_URL}/storyblok/spaces`, {
      headers,
    })
    const data = await response.json()

    spaces.value = data?.spaces || []
    return spaces.value
  }

  async function migrateBlocks() {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await generateHeaders()
        headers['Content-Type'] = 'application/json'

        const response = await fetch(`${import.meta.env.VITE_API_URL}/blocks/migrate`, {
          method: 'POST',
          body: JSON.stringify({
            targetSpace: space.value.id,
          }),
          headers,
        })

        if (response.ok) {
          const { jobId, statusUrl } = await response.json()
          delete headers['Content-Type']

          // We wait a few seconds before we start polling the job
          await new Promise((resolve) => setTimeout(resolve, 2000))

          let retries = 5
          let indexNextLog = 0
          const intervalId = setInterval(async () => {
            const statusResponse = await fetch(statusUrl, {
              headers,
            })
            if (statusResponse.ok) {
              // We reset the retries
              retries = 5

              const job = await statusResponse.json()
              if (job.steps.length > indexNextLog) {
                const logSubset = job.steps.slice(indexNextLog)
                generationLogs.value.push(...logSubset)
                indexNextLog = job.steps.length
              }

              if (['completed', 'failed'].includes(job.status)) {
                clearInterval(intervalId)

                if (job.status === 'failed') {
                  reject(new Error(`Block migration failed.`))
                } else {
                  await setDefaultPresets()
                  resolve()
                }
              }
            } else {
              retries--
              if (retries < 0) {
                clearInterval(intervalId)
                reject(new Error(`Could not get job status for job ${jobId}.`))
              }
            }
          }, 2000)
        } else {
          reject(
            new Error(`Failed calling blocks migration endpoint. HTTP status: ${response.status}.`),
          )
        }
      } catch (e) {
        reject(e)
      }
    })
  }

  async function setDefaultPresets() {
    const headers = await generateHeaders()

    generationLogs.value.push({
      timestamp: new Date().toISOString(),
      message: `Setting default presets in migrated blocks`,
    })

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/storyblok/spaces/${space.value.id}/presets`,
      {
        headers,
      },
    )

    if (response.ok) {
      const { presets } = await response.json()
      const blocksPresets = {}
      presets.forEach((preset) => {
        if (!blocksPresets[preset.component_id]) {
          blocksPresets[preset.component_id] = []
        }
        blocksPresets[preset.component_id].push(preset)
      })

      const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

      headers['Content-Type'] = 'application/json'
      const blocks = Object.entries(blocksPresets)
      let i = 0
      for (const blockId in blocksPresets) {
        let presetId = blocksPresets[blockId][0].id
        if (blocksPresets[blockId].length > 1) {
          const defaultPreset = blocksPresets[blockId].find((preset) =>
            preset.name.includes('default'),
          )
          if (defaultPreset) {
            presetId = defaultPreset.id
          }
        }

        const updateResponse = await fetch(
          `${import.meta.env.VITE_API_URL}/storyblok/spaces/${space.value.id}/components/${blockId}`,
          {
            method: 'PUT',
            body: JSON.stringify({
              preset_id: presetId,
            }),
            headers,
          },
        )
        if (!updateResponse.ok) {
          generationLogs.value.push({
            timestamp: new Date().toISOString(),
            message: `Could not set default preset for block ${blockId} (${i + 1}/${blocks.length})`,
          })
        } else {
          generationLogs.value.push({
            timestamp: new Date().toISOString(),
            message: `Default preset set (${i + 1}/${blocks.length})`,
          })
        }
        // We wait a bit between requests, to avoid getting a 429 Too Many Requests response
        await sleep(500)
        i++
      }
    } else {
      generationLogs.value.push({
        timestamp: new Date().toISOString(),
        message: `Could not fetch presets in target space.`,
      })
    }
  }

  async function configureSpace() {
    const headers = await generateHeaders()

    generationLogs.value.push({
      timestamp: new Date().toISOString(),
      message: `Updating target space's preview URL so it uses our editor`,
    })

    generationLogs.value.push({
      timestamp: new Date().toISOString(),
      message: `Updating target space's default content type block`,
    })

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/storyblok/spaces/${space.value.id}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          space: {
            id: space.value.id,
            domain: import.meta.env.VITE_STORYLOK_PREVIEW_URL,
            default_root: 'Page',
          },
        }),
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
      },
    )

    if (response.ok) {
      generationLogs.value.push({
        timestamp: new Date().toISOString(),
        message: `Target space configured successfully`,
      })

      // We remove the default pages in the space, if they exist
      const params = new URLSearchParams({
        contain_component: DEFAULT_BLOCKS_TO_DELETE.join(','),
        story_only: true,
        with_parent: 0, // Only delete stories at root level
      })
      const searchResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/storyblok/spaces/${space.value.id}/stories?${params.toString()}`,
        {
          headers,
        },
      )
      if (searchResponse.ok) {
        const { stories } = await searchResponse.json()
        for (const story of stories) {
          const deleteResponse = await fetch(
            `${import.meta.env.VITE_API_URL}/storyblok/spaces/${space.value.id}/stories/${story.id}`,
            {
              method: 'DELETE',
              headers,
            },
          )
          if (deleteResponse.ok) {
            generationLogs.value.push({
              timestamp: new Date().toISOString(),
              message: `Deleted unused story "${story.slug}"`,
            })
          }
        }
      } else {
        generationLogs.value.push({
          timestamp: new Date().toISOString(),
          message: `Could not fetch unused stories in target space. Skipping clean-up.`,
        })
      }

      // We remove the default blocks in the space
      const componentsResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/storyblok/spaces/${space.value.id}/components`,
        {
          headers,
        },
      )
      if (componentsResponse.ok) {
        const { components } = await componentsResponse.json()
        for (const component of components) {
          // We make sure the match is case sensitive
          if (DEFAULT_BLOCKS_TO_DELETE.includes(component.name)) {
            const deleteResponse = await fetch(
              `${import.meta.env.VITE_API_URL}/storyblok/spaces/${space.value.id}/components/${component.id}`,
              {
                method: 'DELETE',
                headers,
              },
            )
            if (deleteResponse.ok) {
              generationLogs.value.push({
                timestamp: new Date().toISOString(),
                message: `Deleted unused block "${component.name}"`,
              })
            } else {
              throw new Error(`Could not delete unused block "${component.name}" in target space.`)
            }
          }
        }
      } else {
        throw new Error('Could not fetch unused blocks in target space.')
      }
    } else {
      throw new Error('Could not configure target space. Check that the access token is valid.')
    }
  }

  async function migrateDatasources() {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const headers = await generateHeaders()
        headers['Content-Type'] = 'application/json'

        const response = await fetch(`${import.meta.env.VITE_API_URL}/datasources/migrate`, {
          method: 'POST',
          body: JSON.stringify({
            targetSpace: space.value.id,
            datasources: DATASOURCES_TO_COPY,
          }),
          headers,
        })

        if (response.ok) {
          const { jobId, statusUrl } = await response.json()
          delete headers['Content-Type']

          // We wait a few seconds before we start polling the job
          await new Promise((resolve) => setTimeout(resolve, 2000))

          let retries = 5
          let indexNextLog = 0
          const intervalId = setInterval(async () => {
            const statusResponse = await fetch(statusUrl, {
              headers,
            })
            if (statusResponse.ok) {
              // We reset the retries
              retries = 5

              const job = await statusResponse.json()
              if (job.steps.length > indexNextLog) {
                const logSubset = job.steps.slice(indexNextLog)
                generationLogs.value.push(...logSubset)
                indexNextLog = job.steps.length
              }

              if (['completed', 'failed'].includes(job.status)) {
                clearInterval(intervalId)

                if (job.status === 'failed') {
                  reject(new Error(`Datasources migration failed.`))
                } else {
                  resolve()
                }
              }
            } else {
              retries--
              if (retries < 0) {
                clearInterval(intervalId)
                reject(new Error(`Could not get job status for job ${jobId}.`))
              }
            }
          }, 2000)
        } else {
          reject(
            new Error(
              `Failed calling datasources migration endpoint. HTTP status: ${response.status}.`,
            ),
          )
        }
      } catch (e) {
        reject(e)
      }
    })
  }

  async function createFolder(slug, name, parent, extraProps) {
    const headers = await generateHeaders()
    headers['Content-Type'] = 'application/json'

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/storyblok/spaces/${space.value.id}/stories`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          story: {
            is_folder: true,
            name: name || slug,
            slug,
            parent_id: parent?.id || 0, // Create the folder at root level if no parent is defined
            ...extraProps,
          },
        }),
      },
    )
    if (response.ok) {
      const { story } = await response.json()

      generationLogs.value.push({
        timestamp: new Date().toISOString(),
        message: `Folder ${slug} created successfully`,
      })

      return story
    } else {
      throw new Error(`Could not create folder "${slug}"`)
    }
  }

  async function copyTheme(stories) {
    const theme = stories.find((story) => story.slug === '_theme')
    if (theme) {
      const newTheme = { ...theme }
      newTheme.parent_id = folderCreated.value.id
      delete newTheme.full_slug
      delete newTheme.group_id

      const headers = await generateHeaders()
      headers['Content-Type'] = 'application/json'
      const themeResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/storyblok/spaces/${space.value.id}/stories`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            story: newTheme,
          }),
        },
      )
      if (themeResponse.ok) {
        const { story } = await themeResponse.json()
        pagesCreated.value.push(story)

        generationLogs.value.push({
          timestamp: new Date().toISOString(),
          message: `_theme copied`,
        })
      } else {
        throw new Error(
          `Could not create _theme in target folder. Http status: ${themeResponse.status}`,
        )
      }
    } else {
      throw new Error(`No _theme found in source folder.`)
    }
  }

  async function copyGlobals(stories, rels) {
    generationLogs.value.push({
      timestamp: new Date().toISOString(),
      message: `Handling _global pages`,
    })
    const globalsFolder = await createFolder('_global', '_global', folderCreated.value, {
      default_root: 'Global',
    })

    const headers = await generateHeaders()
    headers['Content-Type'] = 'application/json'

    for (const globalStory of rels) {
      const fullStory = stories.find((story) => story.full_slug === globalStory.full_slug)
      if (!fullStory) {
        throw new Error(`Could not find page for "_global/${globalStory.slug}" in source folder.`)
      }

      const newStory = { ...fullStory }
      newStory.parent_id = globalsFolder.id
      delete newStory.full_slug
      delete newStory.group_id

      // We remove the links for pages that we're not going to copy
      newStory.content.blocks.forEach((block) => {
        if (block.component === 'PageHeaderWithMenu') {
          const headerLinks = block.menuItems.filter(
            (link) =>
              link.externalLinkUrl === '/' ||
              pages.value.includes(link.externalLinkUrl.substring(1)),
          )
          block.menuItems = headerLinks
        } else if (block.component === 'FooterMenuTextText') {
          const footerLinks = block.LinkList[0].links.filter(
            (link) =>
              link.externalLinkUrl === '/' ||
              pages.value.includes(link.externalLinkUrl.substring(1)),
          )
          block.LinkList[0].links = footerLinks
        }
      })

      const globalResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/storyblok/spaces/${space.value.id}/stories`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            story: newStory,
          }),
        },
      )
      if (globalResponse.ok) {
        const { story } = await globalResponse.json()
        pagesCreated.value.push(story)

        generationLogs.value.push({
          timestamp: new Date().toISOString(),
          message: `${newStory.slug} copied`,
        })
      } else {
        throw new Error(`Failed copying "_global/${newStory.slug}" to target folder.`)
      }
    }
  }

  async function copyStory(slug, stories, rels, parentFolder) {
    const storyToCopy = stories.find((story) =>
      slug === 'homepage' ? story.is_startpage : story.slug === slug,
    )
    if (!storyToCopy) {
      throw new Error(`No page matches slug "${slug}". Aborting copy operation.`)
    }

    const newStory = { ...storyToCopy }

    newStory.parent_id = parentFolder.id
    delete newStory.full_slug
    delete newStory.group_id

    newStory.content.dataCompanySlug = company.value.slug
    newStory.content.blocks =
      storyToCopy.content.blocks?.reduce((accumulator, block) => {
        delete block._editable

        if (newStory.is_startpage && block.sectionLinkId?.includes('-hero-')) {
          // This block is part of a hero banner template, we only add it if it belongs to the template we're using
          if (block.sectionLinkId.startsWith(heroBannerTemplate.value)) {
            accumulator.push(block)
          }
        } else if (newStory.is_startpage && block.sectionLinkId?.startsWith('testimonials-')) {
          // This block is part of the testimonials template, we only add it if it belongs to the template we're using
          if (block.sectionLinkId.includes(testimonialsTemplate.value)) {
            accumulator.push(block)
          }
        } else if (
          block.component.startsWith('CompanyJobAlert') &&
          !pages.value.includes('_default/_unsubscribe')
        ) {
          // We remove the job alert blocks if we're not generating the unsubscribe page
          console.log(
            `${block.component} removed in page "${slug}" because job alerts are disabled`,
          )
        } else {
          if (block.component === 'GlobalReference') {
            // We update the global references
            const oldGlobal = rels.find((global) => global.uuid === block.reference)
            const newGlobal = pagesCreated.value.find((global) => global.slug === oldGlobal.slug)
            block.reference = newGlobal.uuid
          }
          accumulator.push(block)
        }

        return accumulator
      }, []) || []

    const headers = await generateHeaders()
    headers['Content-Type'] = 'application/json'
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/storyblok/spaces/${space.value.id}/stories`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          story: newStory,
        }),
      },
    )
    if (response.ok) {
      const { story } = await response.json()
      pagesCreated.value.push(story)

      generationLogs.value.push({
        timestamp: new Date().toISOString(),
        message: `${newStory.slug} copied`,
      })
    } else {
      throw new Error(`Could not copy "${newStory.slug}" to target folder.`)
    }
  }

  async function copySourceFolder() {
    generationLogs.value.push({
      timestamp: new Date().toISOString(),
      message: `Creating "${siteSlug.value}" folder in "${space.value.name}"`,
    })

    folderCreated.value = await createFolder(siteSlug.value, tooStore.site.value.name, null)

    const params = new URLSearchParams({
      starts_with: `${import.meta.env.VITE_STORYBLOK_SOURCE_FOLDER}/`,
      resolve_relations: 'GlobalReference.reference',
      resolve_level: 2,
      token: import.meta.env.VITE_STORYBLOK_CDN_TOKEN,
      version: import.meta.env.VITE_ENV === 'production' ? 'published' : 'draft',
      cv: Date.now(),
    })
    const response = await fetch(`https://api.storyblok.com/v2/cdn/stories?${params.toString()}`)
    if (response.ok) {
      const { stories, rels } = await response.json()

      generationLogs.value.push({
        timestamp: new Date().toISOString(),
        message: `Fetched source folder. Starting to copy pages...`,
      })

      // First, we copy the theme
      await copyTheme(stories)

      // Then, we create the globals folder
      await copyGlobals(stories, rels)

      // We copy error-404 page
      // TODO Handle link inside 404
      await copyStory('error-404', stories, rels, folderCreated.value)

      let defaultFolder = null
      for (const page of pages.value) {
        let slug = page
        let folder = folderCreated.value

        if (slug.startsWith('_default/')) {
          slug = slug.split('/')[1]
          if (!defaultFolder) {
            defaultFolder = await createFolder('_default', '_default', folderCreated.value)
          }
          folder = defaultFolder
        }

        await copyStory(slug, stories, rels, folder)
      }
    } else {
      throw new Error('Could not fetch pages from source folder. Aborting copy operation.')
    }
  }

  async function generateAccessToken() {
    if (
      space.value.id != import.meta.env.VITE_STAGING_SPACE_ID &&
      space.value.id != import.meta.env.VITE_PRODUCTION_SPACE_ID
    ) {
      generationLogs.value.push({
        timestamp: new Date().toISOString(),
        message: `Generating a new access token for the space "${space.value.name}".`,
      })

      // We create a new access token
      const headers = await generateHeaders()
      headers['Content-Type'] = 'application/json'
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/storyblok/spaces/${space.value.id}/api_keys`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            api_key: {
              access: 'private',
              name: 'Hellopublic',
            },
          }),
        },
      )
      let token = space.value.first_token
      if (response.ok) {
        const apiKey = await response.json()
        token = apiKey.token
      } else {
        generationLogs.value.push({
          timestamp: new Date().toISOString(),
          message: `Could not create a new access token for space "${space.value.name}". Using default token instead.`,
        })
      }

      generationLogs.value.push({
        timestamp: new Date().toISOString(),
        message: `Storing the new access token in the Publisher.`,
      })

      const tokenSaved = await tooStore.updateStoryblokToken(token)
      if (tokenSaved) {
        generationLogs.value.push({
          timestamp: new Date().toISOString(),
          message: `Access token updated successfully in the Publisher.`,
        })
      } else {
        throw new Error('Could not update access token in the Publisher.')
      }
    }
  }

  async function generateHeaders() {
    const accessToken = await getAccessTokenSilently()
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    }
    if (siteSlug.value) {
      headers['JR-Site'] = siteSlug.value
    }
    return headers
  }

  return {
    siteSlug,
    space,
    company,
    pages,
    heroBannerTemplate,
    testimonialsTemplate,
    spaces: computed(() => spaces.value),
    progress: computed(() => progress.value),
    generationLogs: computed(() => generationLogs.value),
    pagesCreated: computed(() => pagesCreated.value),
    folderCreated: computed(() => folderCreated.value),
    error: computed(() => error.value),
    checkIfSiteExists,
    generateSite,
    restart,
  }
})
